/**
 * Grant service — talks to /admin/grants endpoints (Phase 2 backend).
 */

import apiService from './api';
import type { WorkCategoryCode } from './workAssignment.service';

export type GrantScope = 'always' | 'window' | 'once';

export interface GrantWindow {
  from: string; // ISO
  to: string;   // ISO
  daysOfWeek?: number[];
  minuteRange?: [number, number];
}

export interface PermissionGrant {
  _id: string;
  userId: { _id: string; name: string; email?: string } | string;
  permission: string;
  category: WorkCategoryCode | null;
  scope: GrantScope;
  window?: GrantWindow;
  reason: string;
  grantedBy: { _id: string; name: string; email?: string } | string;
  expiresAt?: string;
  consumedAt?: string;
  isActive: boolean;
  revokedAt?: string;
  revokedBy?: { _id: string; name: string; email?: string } | string;
  revokeReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGrantPayload {
  userId: string;
  permission: string;
  category?: WorkCategoryCode | null;
  scope: GrantScope;
  window?: GrantWindow;
  reason: string;
  expiresAt?: string;
}

export interface MyEffectivePermissions {
  permissions: string[];
  isSuperAdmin: boolean;
  activeGrants: PermissionGrant[];
}

const grantService = {
  /** List grants (system-wide, paginated). Pass userId to filter by user. */
  list: async (params?: {
    userId?: string;
    active?: boolean;
    page?: number;
    limit?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params?.userId) qs.set('userId', params.userId);
    if (params?.active !== undefined) qs.set('active', String(params.active));
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const url = `/admin/grants${qs.toString() ? `?${qs}` : ''}`;
    return apiService.get<{ data: PermissionGrant[]; pagination: { page: number; limit: number; total: number; pages: number } }>(url);
  },

  get: async (id: string) =>
    apiService.get<{ data: PermissionGrant }>(`/admin/grants/${id}`),

  create: async (payload: CreateGrantPayload) =>
    apiService.post<{ data: PermissionGrant }>('/admin/grants', payload),

  revoke: async (id: string, reason: string) =>
    apiService.post<{ data: PermissionGrant }>(`/admin/grants/${id}/revoke`, { reason }),

  /** Current user's effective permissions + active grants */
  myEffective: async () =>
    apiService.get<{ data: MyEffectivePermissions }>('/admin/grants/me'),

  /**
   * Lightweight admin-user list for the Grant target picker.
   * Returns ALL active admins (no shift filtering); requires admin_grant_create
   * to call. Used by `CreateGrantDialog`.
   */
  listAdmins: async (): Promise<
    Array<{ _id: string; name: string; email: string; isSuperAdmin: boolean }>
  > => {
    const res = await apiService.get<{
      success?: boolean;
      data: Array<{ _id: string; name: string; email: string; isSuperAdmin: boolean }>;
    }>('/admin/grants/admins');
    // apiService may unwrap {status,data} but our envelope is {success,data};
    // handle both shapes defensively.
    const list = (res as any)?.data ?? (res as any) ?? [];
    return Array.isArray(list) ? list : [];
  },
};

export default grantService;
